import { useState } from 'react';

import InputFloating from '@/components/atoms/InputFloating';
import TextAreaFloating from '@/components/atoms/TextAreaFloating';

import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useParams } from 'react-router-dom';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from "react-hook-form";
import useCreateProject from '@query/project/useCreateProject';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";


type Props = {
    onCreateProject?: (project: { name: string, id: string }) => void;
}

const AddProjectModal: React.FC<Props> = ({
    onCreateProject
}) => {
    const { orgId } = useParams();

    const [show, setShow] = useState(false);

    const handleOpen = () => {
        setShow(true);
    }
    const handleClose = () => {
        reset({
            name: "",
            challenge: "",
        });
        setShow(false);
    }

    const schema = z.object({
        name: z.string({ required_error: 'Name is required' }),
        challenge: z.string({ required_error: "Description is required" }),
    });

    type inputFields = z.infer<typeof schema>;
    const {
        register,
        handleSubmit,
        formState,
        reset
    } = useForm<inputFields>({ resolver: zodResolver(schema) });
    const { errors } = formState;

    const { isPending: isCreatingProject, mutateAsync: createProject } = useCreateProject();

    const onSubmit = async (data: inputFields) => {
        const requestBody = {
            organisation_id: Number(orgId),
            name: data.name,
            challenge: data.challenge,
            crm_id: null,
            crm_system: null,
            crm_project_id: null,
        }

        const result = await createProject(requestBody);

        const projectId = result?.data?.id;
        if(projectId && onCreateProject){
            onCreateProject({ name: data.name, id: projectId });
            SuccessPopup("Project has been added to the list.");
        }

        handleClose();
    }


    return (
        <>
            {(isCreatingProject) && (<LoaderWithBackdrop />)}
            
            <Dialog open={show} onOpenChange={handleClose} defaultOpen={false}>
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle className='m-0 font-bold text-start'>
                            Create New Project
                        </DialogTitle>
                        <DialogDescription>
                            Fill in the project details below
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] pe-3">
                        <div className="px-1">
                            <div className="m-0 mb-3">
                                <InputFloating
                                    label="Project Name"
                                    register={register('name')}
                                    errorMessage={errors.name?.message}
                                    showError={true}
                                />
                            </div>
                            <div className="m-0 mb-3">
                                <TextAreaFloating
                                    label="Project Description"
                                    register={register('challenge')}
                                    errorMessage={errors.challenge?.message}
                                    showError={true}
                                />
                            </div>
                        </div>
                    </ScrollArea>

                    <div className="flex justify-end w-full">
                        <div className="w-full mb-1 sm:mb-0 sm:me-2 sm:w-auto">
                            <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
                        </div>
                        <div className="w-full sm:w-auto">
                            <Button type="submit" onClick={() => handleSubmit(onSubmit)()}>Submit</Button>
                        </div>
                    </div>

                </DialogContent>
            </Dialog>

            <Button onClick={handleOpen}>Create New Project</Button>
        </>
    )
}

export { AddProjectModal };
